import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  addTodoShares,
  changeTodoTaskStatus,
  createTodoList,
  createTodoTask,
  deleteTodoList,
  deleteTodoTask,
  deleteTodoTaskAttachment,
  getTodoLists,
  getTodoShares,
  getTodoTaskAttachments,
  getTodoTasks,
  removeTodoShare,
  updateTodoList,
  updateTodoTask,
  updateTodoTaskDescription,
  uploadTodoTaskAttachments,
} from '../api/todoApi'
import type { TodoListUpsert, TodoTask, TodoTaskUpsert } from '../types'

const LIST_KEY = 'todo-lists'
const TASK_KEY = 'todo-tasks'
const SHARE_KEY = 'todo-shares'
const ATTACHMENT_KEY = 'todo-task-attachments'

/**
 * Liste seçeneklerini **yalnız istendiğinde** çeker (123 — ana sayfadaki "Not Ekle" pop-up'ı).
 *
 * <p>Anahtar {@link useTodoLists} ile **aynıdır**: iki yüzey tek önbelleği paylaşır, pop-up'tan
 * eklenen not Notlarım ekranına açılışta zaten yansır. `enabled` olmadan ana sayfa her açılışta
 * kullanıcının listelerini boşuna çekerdi.
 */
export function useTodoListOptions(enabled: boolean) {
  return useQuery({ queryKey: [LIST_KEY], queryFn: getTodoLists, enabled })
}

export interface QuickNoteArgs {
  listId: number
  name: string
  files: File[]
}

export interface QuickNoteResult {
  task: TodoTask
  /** Not oluştu ama ek yüklemesi düştü → çağıran uyarı gösterir (not geri alınmaz). */
  filesFailed: boolean
}

/**
 * **Ana sayfadan not ekle** (123): seçilen listeye madde oluşturur, ardından dosyaları ona yükler.
 *
 * <p>Dosya yüklemek için maddenin id'si gerekir, bu yüzden iki istek sıralıdır — kalıbın emsali
 * `useCreateStandaloneAction` (Görev Oluştur). Ek adımı patlarsa not **kalır** ve sonuç
 * `filesFailed` ile bildirilir: kullanıcıya "kaydedildi" deyip dosyayı sessizce kaybetmek yoktur.
 */
export function useQuickAddNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ listId, name, files }: QuickNoteArgs): Promise<QuickNoteResult> => {
      const task = await createTodoTask(listId, { name })
      if (files.length === 0) return { task, filesFailed: false }
      try {
        await uploadTodoTaskAttachments(task.id, files)
        return { task, filesFailed: false }
      } catch {
        return { task, filesFailed: true }
      }
    },
    onSuccess: (_result, { listId }) => {
      qc.invalidateQueries({ queryKey: [TASK_KEY, listId] })
      // Kart sayaçları madde sayısına bağlı → liste sorgusu da bayatlar.
      qc.invalidateQueries({ queryKey: [LIST_KEY] })
    },
  })
}

/**
 * Bir maddenin dosya ekleri + yükle/kaldır (123). Liste sorgusu yalnız pop-up açıkken koşar
 * (`enabled`). Her mutasyon sonrası **madde listesi de** bayatlar: satırdaki ek göstergesi
 * (`attachmentCount` — ataç rozeti) oradan okunur.
 */
export function useTodoTaskAttachments(taskId: number | null, listId: number) {
  const qc = useQueryClient()
  const list = useQuery({
    queryKey: [ATTACHMENT_KEY, taskId],
    queryFn: () => getTodoTaskAttachments(taskId as number),
    enabled: taskId != null,
  })
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [ATTACHMENT_KEY, taskId] })
    qc.invalidateQueries({ queryKey: [TASK_KEY, listId] })
  }
  const upload = useMutation({
    mutationFn: (files: File[]) => uploadTodoTaskAttachments(taskId as number, files),
    onSuccess: invalidate,
  })
  const remove = useMutation({
    mutationFn: (attachmentId: number) => deleteTodoTaskAttachment(taskId as number, attachmentId),
    onSuccess: invalidate,
  })
  return { list, upload, remove }
}

/**
 * Notlarım sunucu-durumu (113).
 *
 * <p><b>Tazeleme ekranın açılışındadır</b>, canlı güncelleme yoktur (spec Q3 — kapsam dışı): başka
 * bir kullanıcının değişikliği ekran açıkken kendiliğinden belirmez. Kullanıcının **kendi** işlemi
 * ise anında yansır, çünkü her yazma sonrası ilgili sorgular bayatlatılır.
 *
 * <p>Liste sayaçları (`taskCount`/`completedTaskCount`) madde değişikliklerinden etkilendiği için
 * madde mutasyonları **liste sorgusunu da** bayatlatır — aksi hâlde kullanıcı detaydan geri
 * döndüğünde eski sayacı görürdü.
 */
export function useTodoLists() {
  const qc = useQueryClient()

  const invalidateLists = () => {
    qc.invalidateQueries({ queryKey: [LIST_KEY] })
  }

  const list = useQuery({ queryKey: [LIST_KEY], queryFn: getTodoLists })

  const create = useMutation({
    mutationFn: (body: TodoListUpsert) => createTodoList(body),
    onSuccess: invalidateLists,
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: TodoListUpsert }) => updateTodoList(id, body),
    onSuccess: invalidateLists,
  })

  const remove = useMutation({
    mutationFn: (id: number) => deleteTodoList(id),
    onSuccess: invalidateLists,
  })

  return { list, create, update, remove }
}

/** Bir listenin maddeleri. Aktif/tamamlanan ayrımını ekran yapar; sunucu tek liste döner. */
export function useTodoTasks(listId: number) {
  const qc = useQueryClient()

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [TASK_KEY, listId] })
    // Liste kartındaki sayaçlar madde sayısına bağlı → onlar da bayatlar.
    qc.invalidateQueries({ queryKey: [LIST_KEY] })
  }

  const list = useQuery({
    queryKey: [TASK_KEY, listId],
    queryFn: () => getTodoTasks(listId),
    enabled: Number.isFinite(listId) && listId > 0,
  })

  const create = useMutation({
    mutationFn: (body: TodoTaskUpsert) => createTodoTask(listId, body),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: TodoTaskUpsert }) => updateTodoTask(id, body),
    onSuccess: invalidate,
  })

  const changeStatus = useMutation({
    mutationFn: ({ id, statusId }: { id: number; statusId: number }) =>
      changeTodoTaskStatus(id, statusId),
    onSuccess: invalidate,
  })

  /**
   * 124 — yorum. Liste **hatada da** tazelenir (`onSettled`): 409 (başkası araya girdi) sonrası
   * pop-up açık kalır ve satırın güncel `modDate`'i okunur; kullanıcı yazdığı metni kaybetmeden
   * yeniden kaydedebilir. Hata toast'ı merkezîdir (`MutationCache`).
   */
  const updateDescription = useMutation({
    mutationFn: ({ task, description }: { task: TodoTask; description: string }) =>
      updateTodoTaskDescription(task.id, description, task.modDate),
    onSettled: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: number) => deleteTodoTask(id),
    onSuccess: invalidate,
  })

  return { list, create, update, updateDescription, changeStatus, remove }
}

/**
 * Bir listenin paylaşımları — "Listeyi Görebilenler".
 *
 * <p>Sorgu yalnız <b>pop-up açıkken</b> koşar (`enabled`): paylaşım listesi ana ekranda
 * gösterilmiyor, her kart için baştan çekmek her açılışta N istek demek olurdu.
 *
 * <p>Ekleme sonrası <b>liste sorgusu da</b> bayatlatılır: kartın "paylaşılan" görünümü ve sahip
 * bilgisi paylaşıma bağlıdır.
 */
export function useTodoShares(listId: number | null) {
  const qc = useQueryClient()

  const list = useQuery({
    queryKey: [SHARE_KEY, listId],
    queryFn: () => getTodoShares(listId as number),
    enabled: listId != null,
    /**
     * 404'te merkezî toast BASILMAZ. Bu uç tek bir sebeple 404 döner — liste artık bu kullanıcıya
     * görünmüyor — ama 30.09.2026'dan beri oraya **iki yol** çıkıyor:
     *
     * 1. Kullanıcı **kendi erişimini** kaldırdı. Susturmanın asıl sebebi budur: ekranda önce
     *    "Erişim kaldırıldı." sonra "Kayıt bulunamadı." çıkıyordu ve ikincisi kullanıcının az önce
     *    bilerek yaptığı işin sonucuydu, hata değil (kullanıcı bildirimi 30.09.2026).
     * 2. **Başkası listeyi sildi** (artık paylaşılan kullanıcı da silebiliyor). Burada kullanıcı
     *    bir bildirim görmez ve pop-up boş kalır. **Bilinçle kabul edildi:** alternatifi, her iki
     *    yolu ayırt etmek için sorguya durum taşımaktı; kazanç, nadir bir yarışta jenerik bir
     *    "kayıt bulunamadı" göstermekten ibaret olurdu. Liste zaten bir sonraki tazelemede
     *    ekrandan düşer.
     */
    meta: { handledCodes: ['NOT_FOUND'] },
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [SHARE_KEY, listId] })
    // Kartın "paylaşılan" görünümü ve sahip bilgisi paylaşıma bağlı → liste sorgusu da bayatlar.
    qc.invalidateQueries({ queryKey: [LIST_KEY] })
  }

  const add = useMutation({
    mutationFn: (userIds: number[]) => addTodoShares(listId as number, userIds),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (userId: number) => removeTodoShare(listId as number, userId),
    onSuccess: invalidate,
  })

  return { list, add, remove }
}
